import { bridge } from './bridge';
import { findMenu } from './anchors';
import { closeInfoModals } from './dialogs';

/** 화면이 바뀐 뒤 떠난 화면이 정리될 틈. */
const SETTLE_MS = 400;

const POLL_MS = 25;
const EXPAND_MS = 250;

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => { setTimeout(resolve, ms); });
}

// 모듈을 바꾸면 사이드바를 다시 그린다. 그것을 기다려 주는 시간.
const RENDER_MS = 3000;

function elements(selector: string): HTMLElement[] {
  return Array.from(document.querySelectorAll<HTMLElement>(selector));
}

/** 그려질 때까지 기다린다. 없으면 시간을 다 쓰고 null 이다. */
async function waitForNode(selector: string, timeoutMs: number): Promise<HTMLElement | null> {
  const deadline = Date.now() + timeoutMs;
  for (;;) {
    const node = document.querySelector<HTMLElement>(selector);
    if (node) return node;
    if (Date.now() >= deadline) return null;
    // eslint-disable-next-line no-await-in-loop
    await sleep(POLL_MS);
  }
}

/**
 * 메뉴를 뒤져 화면 항목을 드러낸다.
 *
 * 메뉴는 세 겹이다 — 상단 모듈, 사이드바 폴더, 그 안의 화면. **화면 앵커는 그
 * 폴더를 펼치기 전에는 DOM 에 아예 없다.** 실제 앱에서 확인한 사실이다.
 *
 * 어느 폴더에 있는지 미리 알 방법이 없다. 카탈로그의 menuPath 는 디렉토리에서
 * 나온 것이라 UI 의 한글 폴더명과 다르다. 사람이 위치를 모를 때 하듯 열어 보며
 * 찾는다. 이미 열린 모듈부터 보므로 같은 모듈 안이면 클릭 몇 번이면 끝난다.
 */
async function revealMenu(routePath: string): Promise<HTMLElement | null> {
  const direct = findMenu(routePath);
  if (direct) return direct;

  // 앱이 어디에 있는지 알려주면 그대로 편다. 모듈 하나, 폴더 하나면 끝난다.
  const chain = bridge().menuChain?.(routePath);
  if (chain?.length) {
    const anchorOf = (id: string) => `[data-el-module="${id}"], [data-el-menu-group="${id}"]`;
    for (let step = 0; step < chain.length; step += 1) {
      // 다음 단계는 앞 단계를 누른 **뒤에** 그려진다. 상단 모듈을 바꾸면 사이드바를
      // 통째로 다시 그리느라 늦다. 한 번 보고 없다고 포기하면 그 모듈의 화면을 전부
      // 놓친다 — 282개 중 220개가 그렇게 "메뉴에 없다" 로 떨어졌다.
      //
      // 기다리는 것은 **다음에 눌러야 할 것**이다. 매번 화면 항목이 나오기를 끝까지
      // 기다리면 단계마다 제한 시간을 통째로 쓴다.
      // eslint-disable-next-line no-await-in-loop
      const node = await waitForNode(anchorOf(chain[step] as string), RENDER_MS);
      if (!node) break;
      node.click();

      const next = chain[step + 1];
      const wanted = next
        ? `[data-el-menu="${routePath}"], ${anchorOf(next)}`
        : `[data-el-menu="${routePath}"]`;
      // eslint-disable-next-line no-await-in-loop
      const appeared = await waitForNode(wanted, RENDER_MS);
      const found = findMenu(routePath);
      if (found) return found;
      if (!appeared) break;
    }
    const opened = findMenu(routePath);
    if (opened) return opened;
  }

  // 알려주지 못하는 앱에서는 열어 보며 찾는다. 못 찾으면 메뉴가 다 펼쳐진 채로
  // 끝나므로, 위쪽 길이 있으면 여기까지 오지 않는 편이 낫다.
  for (const group of elements('[data-el-menu-group]')) {
    group.click();
    await sleep(EXPAND_MS);
    const found = findMenu(routePath);
    if (found) return found;
  }

  for (const module of elements('[data-el-module]')) {
    module.click();
    await sleep(EXPAND_MS);
    const opened = findMenu(routePath);
    if (opened) return opened;
    for (const group of elements('[data-el-menu-group]')) {
      group.click();
      await sleep(EXPAND_MS);
      const found = findMenu(routePath);
      if (found) return found;
    }
  }
  return null;
}

/**
 * 메뉴 항목을 실제로 클릭한다. 탭 등록·권한 확인·currentPage 설정이
 * 사용자가 눌렀을 때와 같은 경로로 일어나야 하므로 라우터를 직접 밀지 않는다.
 */
export async function openScreen(routePath: string, timeoutMs = 5000): Promise<void> {
  const item = await revealMenu(routePath);
  if (!item) throw new Error(`메뉴에 ${routePath} 가 없다. 권한이 없거나 경로가 틀렸다`);
  item.click();

  const deadline = Date.now() + timeoutMs;
  while (Date.now() < deadline) {
    if (bridge().currentPath() === routePath) {
      // 경로가 바뀌었다고 화면이 다 선 것은 아니다. 떠난 화면은 아직 정리 중이고,
      // 그 사이에 다음 일을 시작하면 `Unable to find node on an unmounted component`
      // 가 난다 — 콤보박스가 사라진 자기 DOM 을 찾는다. 사람이 쓸 때는 안 나던 것이
      // 에이전트가 빨라서 드러난다.
      //
      // 화면마다 생명주기를 손보는 것보다 여기서 한 번 쉬는 편이 싸다. 이 시간은
      // 조회 한 번에 한 번뿐이라 전체에서 눈에 띄지 않는다.
      await sleep(SETTLE_MS);
      // 화면을 열면 그 화면이 알림 창을 띄우기도 한다. 일하는 창이 아니면 닫는다.
      closeInfoModals();
      return;
    }
    await sleep(POLL_MS);
  }
  throw new Error(`${routePath} 가 ${timeoutMs}ms 안에 열리지 않았다`);
}
